/**
 * The header bell — docs/13 § UI.
 *
 *   "The bell in the header carries a count and the accessible name `খবর — {n}টি নতুন`."
 *
 * NT1 does not draw a bell: you are already in the centre when you are looking at it. The
 * bell belongs to every OTHER screen's app bar, which WP-D, WP-C and WP-G own. So this is
 * exported for them to mount rather than drawn into NT1, and it lives here because the
 * count and the route are WP-M's.
 *
 * THE ACCESSIBLE NAME IS NOT WRITTEN HERE, and cannot be. `@oprappo/ui`'s `IconButton`
 * reads it from `ariaLabel.bell(count)` in `@oprappo/tokens` and offers no way to pass a
 * different string — docs/01 § accessible names: "Do not invent aria-label text; use the
 * specified string." `ariaLabel.bell` also converts the count to Bangla digits, which is
 * why the caller passes a number and never a formatted string.
 *
 * The visible count pill is `@oprappo/ui`'s `CountBadge`, which takes the numeral already
 * in Bangla — Manrope has no Bangla digits (docs/02 § numerals).
 */
import { useNavigate } from 'react-router-dom';
import { CountBadge, IconButton } from '@oprappo/ui';
import { toBanglaDigits } from '@oprappo/tokens';

export interface NotificationBellProps {
  /** Unread notifications. `0` renders the bell with no pill, still correctly named. */
  count: number;
  /** Icon size in px, from the app bar being built. The design system draws 22. */
  size?: number | undefined;
  stroke?: string | undefined;
}

export function NotificationBell({ count, size = 22, stroke }: NotificationBellProps): JSX.Element {
  const navigate = useNavigate();
  return (
    <span className="relative inline-flex">
      <IconButton
        control="bell"
        count={count}
        size={size}
        {...(stroke === undefined ? {} : { stroke })}
        onClick={() => navigate('/notifications')}
      />
      {count > 0 ? (
        // The pill is decoration: the count is already in the button's accessible name,
        // and announcing it twice is a defect, not redundancy.
        <span className="absolute -top-[6px] -right-[8px] pointer-events-none" aria-hidden>
          <CountBadge countBn={toBanglaDigits(count)} />
        </span>
      ) : null}
    </span>
  );
}
